import React, { useRef } from 'react'
import classNames from 'classnames'
import { FormattedMessage } from 'react-intl'
import {
	Button,
	RadioButton,
	TextInput,
	TextArea,
	Form,
} from '@wf-wfria/pioneer-core'
import { ModalDialog } from './ModalDialog'
import { SystemMessage } from './SystemMessages'

export function FeedbackForm({
	feedbackEmail,
	allowContactRegardingFeedback,
	showFeedbackForm,
	showFeedbackSubmitButton,
	feedbackMessage,
	onUpdateInput,
	onUpdateAllowContactRegardingFeedback,
	onSubmitFeedback,
	onHideFeedback,
	intl,
	onCustomTextInputValidator,
}) {
	const { formatMessage } = intl
	const emailRef = useRef(null)
	const valueMissing = formatMessage({ id: 'required-field.empty.error.label' })

	function onChange({ id, value }) {
		onUpdateInput(id, value)
	}

	function invokeOnUpdateAllowContactRegardingFeedback({ value }) {
		const allowContact = value === 'yes'
		if (!allowContact) {
			if (emailRef.current?.validate) {
				emailRef.current.validate('reset')
			}
		}
		onUpdateAllowContactRegardingFeedback(allowContact)
	}

	function onTextInputValidator(elementId, inputValue) {
		return onCustomTextInputValidator(elementId, inputValue, formatMessage)
	}

	function onSubmitFeedbackButton(e) {
		e.preventDefault()
		onSubmitFeedback()
	}

	function feedbackTextValidator(value) {
		const regex = /^[A-Za-z0-9:\(\)\[\]\$\@\#\?\/\.\,\'\s\-]{1,1000}$/
		if (!regex.test(value)) {
			return formatMessage({
				id: 'feedback-form-content.validation.message.invalid-textarea-content-length',
			})
		}
		return undefined
	}

	return (
		<ModalDialog
			className="FeedbackForm"
			onClose={onHideFeedback}
			open={showFeedbackForm}
			title={formatMessage({
				id: 'feedback-form.title.label',
			})}
			width="950px"
		>
			<Form onSubmit={onSubmitFeedbackButton}>
				<SystemMessage
					show={feedbackMessage?.show}
					iconKind={feedbackMessage?.iconKind}
					messageId={feedbackMessage?.message}
				/>
				<p>
					<FormattedMessage id="feedback-form-content.feedback-information-1.text" />
				</p>
				<p>
					<FormattedMessage id="feedback-form-content.feedback-information-2.text" />
				</p>
				<TextArea
					id="feedbackText"
					name="feedbackText"
					label="Feedback"
					required
					onValueChange={onChange}
					characterCounter={{
						counterMax: 1000,
					}}
					customValidators={[feedbackTextValidator]}
				/>

				<div className="FeedbackFormQuestion">
					<span>
						<FormattedMessage id="feedback-form-content.feedback-question.text" />
					</span>
					<RadioButton
						label={formatMessage({
							id: 'feedback-form-content.feedback-question.yes.label',
						})}
						value={'yes'}
						name="yes"
						onValueChange={invokeOnUpdateAllowContactRegardingFeedback}
						checked={allowContactRegardingFeedback === true}
						data-testid="radio-yes"
					/>
					<RadioButton
						label={formatMessage({
							id: 'feedback-form-content.feedback-question.no.label',
						})}
						value={'no'}
						name="no"
						onValueChange={invokeOnUpdateAllowContactRegardingFeedback}
						checked={allowContactRegardingFeedback === false}
					/>
				</div>
				<TextInput
					aria-label={formatMessage({
						id: 'feedback-form-content.feedback-email.aria-label',
					})}
					id="feedbackEmail"
					ref={emailRef}
					name="feedbackEmail"
					className="FeedbackEmailInput"
					label={formatMessage({
						id: 'feedback-form-content.feedback-email.label',
					})}
					placeholder={formatMessage({
						id: 'feedback-form-content.feedback-email.placeholder',
					})}
					aria-required={true}
					disabled={!allowContactRegardingFeedback}
					value={feedbackEmail?.value}
					onValueChange={onChange}
					customValidators={[
						(value) => onTextInputValidator('feedbackEmail', value),
					]}
					nativeValidationMessages={{
						valueMissing,
					}}
				/>
				<div>
					<Button
						className="FeedbackFormFormFooterSubmitButton"
						label={formatMessage({
							id: 'feedback-form-footer.submit.button.label',
						})}
						type="submit"
						kind="primary"
						disabled={!showFeedbackSubmitButton}
					/>
					<Button
						className="FeedbackFormFormFooterCancelButton"
						type="reset"
						label={formatMessage({
							id: 'feedback-form-footer.cancel.button.label',
						})}
						kind="chromeless"
						onClick={onHideFeedback}
					/>
				</div>
			</Form>
		</ModalDialog>
	)
}
